import { Mic, X, Sparkles, Lock } from 'lucide-react';
import { useState } from 'react';
import api from '@/lib/api';

/**
 * VoiceActivateModal — first-touch conversion modal that appears the very
 * first time a doctor taps the 🎙️ Voice-mode button. Shows once (per
 * doctor account); subsequent taps go straight into Voice mode.
 *
 * Positions the feature as a global-first medical innovation and shows
 * the pricing tier card up-front (Free 3 · Turbo +10/mo · ₹5/DHF beyond).
 */
export default function VoiceActivateModal({ open, onClose, onActivated, quota }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  if (!open) return null;

  const activate = async () => {
    setBusy(true); setErr('');
    try {
      const { data } = await api.post('/dashboard/voice/activate', {});
      onActivated && onActivated(data);
    } catch (e) {
      setErr('Could not activate — please try again.');
      setBusy(false);
    }
  };

  const freeRem = quota?.free_remaining ?? 3;
  const isTurbo = !!quota?.is_turbo;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-testid="voice-activate-modal">
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-700 z-10"
          data-testid="voice-modal-close"
          aria-label="Close"
        >
          <X size={16} />
        </button>

        {/* Hero banner */}
        <div className="bg-gradient-to-br from-sky-600 via-violet-600 to-amber-500 px-6 pt-8 pb-6 text-white">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/20 border border-white/30 text-[10px] font-bold uppercase tracking-widest mb-4">
            <Sparkles size={10} /> Global First · Medical Innovation
          </div>
          <h2 className="text-2xl font-bold leading-tight mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Voice-Enabled Prescriptions.
          </h2>
          <p className="text-sm text-white/90 leading-snug">Hands-free. Sub-minute. Zero typing.</p>

          {/* Animated visual — CSS-only pulse dot with sound-wave rings */}
          <div className="mt-5 flex items-center justify-center h-24 relative" data-testid="voice-modal-hero-anim">
            <span className="absolute w-24 h-24 rounded-full border-2 border-white/30 animate-ping"></span>
            <span className="absolute w-16 h-16 rounded-full border-2 border-white/50 animate-pulse"></span>
            <span className="relative w-16 h-16 rounded-full bg-white/95 flex items-center justify-center shadow-2xl">
              <Mic className="text-sky-600" size={28} strokeWidth={2.4} />
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {/* Benefits */}
          <ul className="space-y-2 mb-4">
            {[
              'Speak the prescription — DocSite fills it for you.',
              'Auto-summarises the visit for your patient.',
              'Locked grammar · zero AI hallucination risk.',
            ].map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700" data-testid={`voice-modal-benefit-${i}`}>
                <span className="mt-0.5 w-4 h-4 rounded-full bg-emerald-100 border border-emerald-200 flex items-center justify-center text-emerald-700 text-[10px] font-bold shrink-0">✓</span>
                <span>{b}</span>
              </li>
            ))}
          </ul>

          {/* Tier card */}
          <div className="rounded-xl border border-sky-200 bg-sky-50/60 p-3.5 mb-4" data-testid="voice-modal-tier-card">
            <p className="text-[10px] font-bold uppercase tracking-widest text-sky-800 mb-1.5">Free For You</p>
            <div className="space-y-1 text-[12.5px] text-slate-700 leading-relaxed">
              <div className="flex items-center gap-1.5">
                <span className="text-amber-500">★★★</span>
                <span><b>First 3 voice files</b> · <span className="text-emerald-700 font-semibold">₹0</span></span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-amber-500">👑</span>
                <span>Turbo doctors get <b>+10/month free</b>{isTurbo ? ' · you\'re Turbo!' : ''}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-slate-400">•</span>
                <span>Beyond free: <b>₹5 per voice-file</b> from your wallet</span>
              </div>
            </div>
          </div>

          {err && <p className="text-xs text-rose-600 mb-2" data-testid="voice-modal-err">{err}</p>}

          {/* CTA */}
          <button
            onClick={activate}
            disabled={busy}
            className="w-full py-3 rounded-xl bg-sky-600 hover:bg-sky-700 text-white text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60 shadow-md"
            data-testid="voice-modal-activate"
          >
            <Mic size={16} />
            {busy ? 'Activating…' : `Activate Voice Prescriptions${freeRem > 0 ? ` · ${freeRem} FREE` : ''}`}
          </button>
          <button
            onClick={onClose}
            className="w-full mt-2 py-1.5 text-xs text-slate-500 hover:text-slate-800 font-semibold"
            data-testid="voice-modal-later"
          >
            Maybe later
          </button>

          {/* Privacy footnote */}
          <p className="mt-4 text-[10.5px] text-slate-500 flex items-start gap-1.5 leading-relaxed">
            <Lock size={10} className="mt-0.5 text-slate-400 shrink-0" />
            <span>One-tap mic permission · uses your browser · nothing leaves the device unless you Generate.</span>
          </p>
        </div>
      </div>
    </div>
  );
}
